import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface LinkItem {
  url: string;
  title: string;
}

export default function CreateStory() {
  const navigate = useNavigate();
  // All hooks are declared unconditionally:
  const [admin, setAdmin] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [madeOn, setMadeOn] = useState('');
  const [urlLink, setUrlLink] = useState('');
  const [picture, setPicture] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [links, setLinks] = useState<LinkItem[]>([{ url: '', title: '' }]);

  useEffect(() => {
    const storedAdmin = localStorage.getItem('admin');
    if (storedAdmin) {
      setAdmin(JSON.parse(storedAdmin));
    } else {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Conditionally render UI based on admin.
  if (!admin) return null; // or a loading indicator

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.size > 100 * 1024) {
      setError('File size exceeds 100KB');
      setPicture(null);
    } else {
      setError('');
      setPicture(file || null);
    }
  };

  const handleLinkChange = (index: number, field: keyof LinkItem, value: string) => {
    const newLinks = [...links];
    newLinks[index][field] = value;
    setLinks(newLinks);
  };

  const addLink = () => {
    setLinks([...links, { url: '', title: '' }]);
  };

  const removeLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);
      setError('');

      // Create the story data object
      const storyData: {
        title: string;
        content: string | null;
        made_on: string | null;
        created_at: string;
        admin_id: any;
        url_links: LinkItem[] | null;
        picture?: string;
      } = {
        title: title.trim(),
        content: content.trim() || null,
        made_on: madeOn || null,
        created_at: new Date().toISOString(),
        admin_id: admin.id,
        // Process links: keep URL but make title optional
        url_links: links
          .filter(link => link.url.trim()) // Only keep links with URLs
          .map(link => ({
            url: link.url.trim(),
            title: link.title.trim() || ' ' // Empty string for missing titles
          }))
      };

      // Handle image upload
      if (picture) {
        const timestamp = Date.now();
        const cleanFileName = picture.name.toLowerCase().replace(/[^a-z0-9.]/g, '_');
        const filePath = `${timestamp}_${cleanFileName}`;

        const { error: uploadError } = await supabase.storage
          .from('picture')
          .upload(filePath, picture, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;
        storyData.picture = filePath;
      }

      // Insert the story
      const { error: insertError } = await supabase
        .from('stories')
        .insert(storyData);

      if (insertError) throw insertError;

      navigate('/story');
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to create story');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#f6faff] dark:bg-[#181825] transition-colors">
      <div className="max-w-4xl mx-auto pt-20 pb-16 px-4">
        {/* Back button added */}
        <button
          onClick={() => navigate(-1)}
          className="mb-4 text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
        >
          &larr; Back
        </button>
        <h2 className="text-3xl font-bold mb-6 text-center text-[#232634] dark:text-white">Create Story</h2>
        {error && <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>}
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-white dark:bg-gray-800 text-[#232634] dark:text-white placeholder:text-gray-400 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow"
          />
          <textarea
            placeholder="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full h-32 bg-white dark:bg-gray-800 text-[#232634] dark:text-white placeholder:text-gray-400 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none shadow"
          />
          <input
            type="date"
            placeholder="Made On"
            value={madeOn}
            onChange={(e) => setMadeOn(e.target.value)}
            className="w-full bg-white dark:bg-gray-800 text-[#232634] dark:text-white placeholder:text-gray-400 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow"
          />
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="w-full bg-white dark:bg-gray-800 text-[#232634] dark:text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow"
          />
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#232634] dark:text-white">URL Links</h3>
              <button
                type="button"
                onClick={addLink}
                className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Link</span>
              </button>
            </div>

            {links.map((link, index) => (
              <div
                key={index}
                className="relative p-4 bg-white dark:bg-gray-800 rounded-lg group animate-fadeIn shadow"
              >
                <div className="grid gap-3">
                  <input
                    type="text"
                    placeholder="Link Title"
                    value={link.title}
                    onChange={(e) => handleLinkChange(index, 'title', e.target.value)}
                    className="w-full bg-gray-100 dark:bg-gray-700 text-[#232634] dark:text-white placeholder:text-gray-400 rounded px-4 py-2"
                  />
                  <input
                    type="url"
                    placeholder="https://..."
                    value={link.url}
                    onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                    className="w-full bg-gray-100 dark:bg-gray-700 text-[#232634] dark:text-white placeholder:text-gray-400 rounded px-4 py-2"
                  />
                </div>

                {links.length > 1 && (
                  <button
                    onClick={() => removeLink(index)}
                    className="absolute -top-2 -right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all opacity-0 group-hover:opacity-100"
                    title="Remove link"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating...' : 'Create Story'}
          </button>
        </div>
      </div>
    </div>
  );
}
